// HOW a provider is dialled — its wire dialect, its auth style, and the
// endpoints it is recognized at. See `../llm-credential.ts` for the file docblock.

import { z } from 'zod';
import { type BYOKProvider, BYOKProvider as Provider } from '../../entities/byok';

// ─── wire + auth style ───────────────────────────────────────────────────────

/**
 * Which protocol the coding agent speaks to the endpoint. Mirrors the runner's
 * backend env-profile split: `anthropic` → `ANTHROPIC_BASE_URL` + an Anthropic
 * auth var; `openai` → `OPENAI_BASE_URL` + `OPENAI_API_KEY`.
 */
export const LlmWire = {
  Anthropic: 'anthropic',
  OpenAI: 'openai',
} as const;
export const LlmWireSchema = z.enum(LlmWire);
export type LlmWire = z.infer<typeof LlmWireSchema>;

/**
 * Which header carries the token on the Anthropic wire — `x-api-key`
 * (`ANTHROPIC_API_KEY`) for direct Anthropic, `bearer` (`ANTHROPIC_AUTH_TOKEN`)
 * for a gateway/OAuth endpoint. The OpenAI wire is always bearer, so the style
 * is informational there. Values match the runner's `AuthStyle` verbatim.
 */
export const LlmAuthStyle = {
  XApiKey: 'x-api-key',
  Bearer: 'bearer',
} as const;
export const LlmAuthStyleSchema = z.enum(LlmAuthStyle);
export type LlmAuthStyle = z.infer<typeof LlmAuthStyleSchema>;

/**
 * Provider → wire policy (reference data, like billing's `PROVIDER_VERIFY_URL`).
 * `defaultBaseUrl` is the endpoint used when the key row carries no `baseUrl`
 * override; `null` means "use the SDK/agent default" (Anthropic direct) or "the
 * key MUST bring its own" (`custom`, `alibaba` — {@link vetProviderBaseUrl} is the
 * seam that refuses one without it). Anthropic is the only x-api-key wire; every
 * other provider is bearer. z.ai routes over its Anthropic-compatible endpoint
 * (the org's coding usage), so it is Anthropic-wire despite a bearer token.
 */
export const PROVIDER_WIRE: Record<
  BYOKProvider,
  { wire: LlmWire; authStyle: LlmAuthStyle; defaultBaseUrl: string | null }
> = {
  [Provider.Anthropic]: {
    wire: LlmWire.Anthropic,
    authStyle: LlmAuthStyle.XApiKey,
    defaultBaseUrl: null,
  },
  [Provider.Zai]: {
    wire: LlmWire.Anthropic,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: 'https://api.z.ai/api/anthropic',
  },
  [Provider.OpenAI]: {
    wire: LlmWire.OpenAI,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: 'https://api.openai.com/v1',
  },
  [Provider.OpenRouter]: {
    wire: LlmWire.OpenAI,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: 'https://openrouter.ai/api/v1',
  },
  [Provider.Cerebras]: {
    wire: LlmWire.OpenAI,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: 'https://api.cerebras.ai/v1',
  },
  [Provider.Minimax]: {
    wire: LlmWire.OpenAI,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: 'https://api.minimax.io/v1',
  },
  [Provider.Gemini]: {
    wire: LlmWire.OpenAI,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai',
  },
  [Provider.Kimi]: {
    wire: LlmWire.OpenAI,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: 'https://api.moonshot.ai/v1',
  },
  // OpenAI-wire bearer third parties (arc 101 slice 03b) — fixed host, no
  // coding-plan alt-path; a key `baseUrl` override (e.g. a gateway) still wins.
  [Provider.Groq]: {
    wire: LlmWire.OpenAI,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: 'https://api.groq.com/openai/v1',
  },
  [Provider.Xai]: {
    wire: LlmWire.OpenAI,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: 'https://api.x.ai/v1',
  },
  [Provider.Deepseek]: {
    wire: LlmWire.OpenAI,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: 'https://api.deepseek.com/v1',
  },
  [Provider.Together]: {
    wire: LlmWire.OpenAI,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: 'https://api.together.xyz/v1',
  },
  [Provider.Fireworks]: {
    wire: LlmWire.OpenAI,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: 'https://api.fireworks.ai/inference/v1',
  },
  /** Anthropic-compatible router gateway; see `FLAT_RATE_ANY_PATH_HOSTS` (#1904). */
  [Provider.Router]: {
    wire: LlmWire.Anthropic,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: 'https://router.ai.developerz.ai',
  },
  // Model Studio's OpenAI-compatible endpoint is scoped to the customer's
  // workspace and region, so there is no host to default to: the key brings its
  // own, and {@link vetProviderBaseUrl} admits only a Model Studio endpoint.
  [Provider.Alibaba]: {
    wire: LlmWire.OpenAI,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: null,
  },
  [Provider.Custom]: {
    wire: LlmWire.OpenAI,
    authStyle: LlmAuthStyle.Bearer,
    defaultBaseUrl: null,
  },
};

/**
 * The `baseURL` a **Vercel-AI-SDK** Anthropic client must be given for an
 * Anthropic-wire endpoint whose stored/default value is `baseUrl`.
 *
 * The two Anthropic SDK conventions disagree on where `/v1` lives, so the same
 * credential cannot be handed to both verbatim:
 *
 * - **Claude Code** — the box's spawned coding agent, reading `ANTHROPIC_BASE_URL`
 *   — appends `/v1/messages` itself. That is why the sealed credential and
 *   {@link PROVIDER_WIRE} carry the BARE ORIGIN.
 * - **`@ai-sdk/anthropic`** appends only `/messages` (3.0.78,
 *   `dist/index.mjs:3736`; its own default `baseURL` ends in `/v1` for exactly
 *   this reason).
 *
 * So every AI-SDK consumer must translate, and this is the one place that knows
 * how. There are THREE legs, not two — the spawned Claude Code child (no
 * translation), the in-cluster factory (`packages/agent`), and the box's
 * IN-PROCESS brains (`apps/runner` review / scout / setup / fan-out planner /
 * verifier judge, all via `modelFromEnvProfile`). #1904 translated at the
 * factory call site alone, which left the box leg dialling `/messages` — a 404
 * on every call, degrading silently to the deterministic floor brain.
 *
 * The rule is DERIVED from the table, not a list of carve-outs (plan 102 slice
 * 04, K8): every endpoint {@link PROVIDER_RECOGNIZED_ENDPOINTS} files under the
 * Anthropic wire is a Claude Code-shaped base (its host serves
 * `<base>/v1/messages`), so every one of them gets `/v1`, whichever provider
 * owns it. That is how a `custom` key on Alibaba's `/apps/anthropic` and a
 * minimax key on `/anthropic` reach the AI SDK correctly without a third
 * special case. The two it began with were measured: probed 2026-08-20,
 * our gateway serves `POST /v1/messages` (401) and not `POST /messages` (404).
 * The 08-20 claim that z.ai's facade answers BOTH shapes was WRONG — it read
 * only the status line. Re-probed unauthenticated 2026-09-05: the bare path
 * `POST https://api.z.ai/api/anthropic/messages` answers HTTP 200 with body
 * `{"code":500,"msg":"404 NOT_FOUND","success":false}` — an application-level
 * 404 wearing a 200 status, so it never worked — while the real route
 * `POST https://api.z.ai/api/anthropic/v1/messages` answers HTTP 401
 * `{"error":{"message":"Authentication parameter not received in Header,
 * unable to authenticate","type":"1001"}}`: the endpoint exists and demands
 * auth. A genuinely foreign Anthropic-wire override still passes verbatim — we
 * cannot know its convention (#3275).
 *
 * Matching is NORMALIZED (origin + pathname, trailing slashes stripped), the
 * same shape the capture-time wire gate uses. A stored
 * `https://router.ai.developerz.ai/` is admitted by that gate, so a raw
 * string comparison here would misfile it as a foreign host and 404 it — the
 * #1759 class this rule exists to close. A genuinely foreign override passes
 * verbatim: we cannot know its convention.
 *
 * Takes a plain `string` rather than a provider: the box resolves an
 * `EnvProfile` that carries a base URL and NO provider, and the rule is a
 * property of the endpoint anyway. Every Anthropic-wire client site applies it,
 * so a key MISLABELLED onto another provider but pointed at our gateway still
 * translates.
 *
 * KNOWN GAP, stated rather than papered over: this matches the canonical origin
 * (scheme + host + port), while the flat-rate classifier matches the HOST alone
 * (`FLAT_RATE_ANY_PATH_HOSTS`). So `http://router…`, `https://router…:8443` and
 * `https://router…/some/path` classify flat-rate — booking $0 per token — and are
 * NOT translated here. That is deliberate: none of those endpoints exists, so
 * appending `/v1` would turn a 404 into a connection failure rather than into a
 * working call. The real fix is to refuse a non-canonical router `base_url` at
 * CAPTURE, where the bad value enters; until then the two gates disagree about
 * which URLs are "the router" and this comment is the record of it.
 */
export function anthropicSdkBaseUrl(baseUrl: string): string {
  const normalized = normalizeLlmEndpoint(baseUrl);
  if (normalized === null) return baseUrl;
  return recognizedEndpoint(normalized)?.wire === LlmWire.Anthropic ? `${normalized}/v1` : baseUrl;
}

/**
 * The documented Anthropic API origin: where an Anthropic-wire key or credential that
 * names no base url dials (#2589). The bare `ANTHROPIC_BASE_URL` form a Claude-Code-shaped
 * client appends `/v1/messages` to; {@link anthropicSdkBase} hands the AI SDK its `/v1`.
 *
 * PINNED, never left to the SDK, because its default reads the ambient env. Measured on
 * the installed `@ai-sdk/anthropic@3.0.118`
 * (`createAnthropic` in `node_modules/@ai-sdk/anthropic/src/anthropic-provider.ts`, through
 * `@ai-sdk/provider-utils@4.0.51` `loadOptionalSetting`): the dialled base is
 * `options.baseURL ?? process.env.ANTHROPIC_BASE_URL ?? 'https://api.anthropic.com/v1'`,
 * read when `createAnthropic` is called. An omitted `baseURL` therefore sends a
 * mothership-sealed key to whatever the process exported.
 */
export const ANTHROPIC_DIRECT_ORIGIN = 'https://api.anthropic.com' as const;

/**
 * The `baseURL` an Anthropic-wire `createAnthropic` client is handed, so it is never
 * omitted ({@link ANTHROPIC_DIRECT_ORIGIN} says why). No base, and the direct origin in
 * any spelling, get `<origin>/v1`; every other base goes through
 * {@link anthropicSdkBaseUrl}, which passes the direct origin verbatim. Which client
 * sites must call it is `ANTHROPIC_CLIENT_SITES`
 * (`packages/agent/src/providers/anthropic-client-sites.test.ts`), which fails a dialling
 * site whose client does not.
 */
export function anthropicSdkBase(baseUrl: string | null | undefined): string {
  if (
    baseUrl === null ||
    baseUrl === undefined ||
    normalizeLlmEndpoint(baseUrl) === ANTHROPIC_DIRECT_ORIGIN
  ) {
    return `${ANTHROPIC_DIRECT_ORIGIN}/v1`;
  }
  return anthropicSdkBaseUrl(baseUrl);
}

/**
 * Exact-match key for endpoint comparison: origin + pathname, trailing slashes
 * stripped. `null` when unparseable — the SSRF gate owns that refusal, so a
 * caller treats null as "not a match" rather than throwing.
 */
export function normalizeLlmEndpoint(raw: string): string | null {
  try {
    const url = new URL(raw);
    return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
  } catch {
    return null;
  }
}

/**
 * First-party endpoints the platform RECOGNIZES for a provider, keyed by the
 * wire that endpoint speaks. Read three ways, all through this one table:
 * {@link wireFor} (the wire a key is SEALED with follows a recognized endpoint,
 * searched across every provider — plan 102 slice 04, K8),
 * {@link anthropicSdkBaseUrl} (`/v1` for every Anthropic-wire entry), and the
 * capture-time wire gate (`validateByokBaseUrlWire` in `@developerz/billing`,
 * #1759, against {@link PROVIDER_SUPPORTED_WIRES}): a stored
 * `baseUrl` naming a recognized endpoint on a wire OTHER than
 * `PROVIDER_WIRE[provider].wire` is refused at capture, because the row would
 * verify green while every in-cluster call broke — the prod zai subscription
 * key that stored the OpenAI-wire coding path after #1757 moved zai to the
 * Anthropic Messages wire.
 *
 * It is not a zai-specific URL check: arming another provider means filling in
 * its sets here. A URL in NO set (a gateway fronting any wire) passes untouched
 * and seals the provider's default wire — the table names first-party
 * endpoints only, so it can never be wrong about a URL it does not list. One
 * URL may appear under several providers but on ONE wire; the index below
 * throws at load if two entries disagree.
 * Matching is exact after normalization (origin + pathname, trailing slashes
 * stripped); an unparseable URL passes, since the SSRF gate owns that refusal.
 * A provider whose sets are all empty is unchanged by construction.
 */
export const PROVIDER_RECOGNIZED_ENDPOINTS: Record<
  BYOKProvider,
  Readonly<Partial<Record<LlmWire, readonly string[]>>>
> = {
  [Provider.Anthropic]: {},
  // Zhipu runs TWO deployments with the same path structure and separate account
  // systems: `api.z.ai` (international) and `open.bigmodel.cn` (the domestic
  // 开放平台). A key issued on one does not authenticate against the other, so a
  // customer holding a domestic key must be able to store the domestic host —
  // and both hosts' endpoints have to be recognized here, or the wire gate reads
  // a domestic Anthropic-wire base_url as unrecognized and the verify probe
  // dials the wrong deployment.
  //
  // Measured 2026-09-14, unauthenticated, with a control: every path below
  // answered 401 on BOTH hosts, while a bogus path on `open.bigmodel.cn`
  // answered `404 NOT_FOUND`. So 401 here distinguishes a real endpoint from an
  // unknown one, the same way the Alibaba Model Studio paths were established.
  //
  // UNMEASURED: `anthropicSdkBaseUrl` appends `/v1` to
  // `open.bigmodel.cn/api/anthropic` by the rule "every recognized Anthropic
  // endpoint is Claude-Code-shaped (serves `<base>/v1/messages`)", not by a probe
  // of `<base>/v1/messages` on that host. Only `api.z.ai` was probed (#3275).
  [Provider.Zai]: {
    [LlmWire.Anthropic]: [
      'https://api.z.ai/api/anthropic',
      'https://open.bigmodel.cn/api/anthropic',
    ],
    [LlmWire.OpenAI]: [
      'https://api.z.ai/api/paas/v4',
      'https://api.z.ai/api/coding/paas/v4',
      'https://open.bigmodel.cn/api/paas/v4',
      'https://open.bigmodel.cn/api/coding/paas/v4',
    ],
  },
  [Provider.OpenAI]: {},
  [Provider.OpenRouter]: {},
  [Provider.Cerebras]: {},
  // MiniMax serves both wires on both deployments (international `minimax.io`,
  // domestic `minimaxi.com`) — the Anthropic-compatible base is MiniMax's own
  // published Claude Code setup (`ANTHROPIC_BASE_URL=…/anthropic`, bearer token).
  // Listed so a minimax key on `/anthropic` seals the Anthropic wire (K8).
  [Provider.Minimax]: {
    [LlmWire.Anthropic]: ['https://api.minimax.io/anthropic', 'https://api.minimaxi.com/anthropic'],
    [LlmWire.OpenAI]: ['https://api.minimax.io/v1', 'https://api.minimaxi.com/v1'],
  },
  [Provider.Gemini]: {},
  [Provider.Kimi]: {},
  [Provider.Groq]: {},
  [Provider.Xai]: {},
  [Provider.Deepseek]: {},
  [Provider.Together]: {},
  [Provider.Fireworks]: {},
  // Model Studio. The OpenAI-wire rows are the FIXED-host endpoints; the
  // workspace-scoped `{WorkspaceId}.<region>.maas.aliyuncs.com` shape cannot be
  // an exact string, and {@link vetProviderBaseUrl} matches it by pattern. The
  // Token Plan host also serves Anthropic Messages under `/apps/anthropic`
  // (probed unauthenticated 2026-09-11: `POST …/apps/anthropic/v1/messages`
  // answers 401 `InvalidApiKey`, a sibling path 404), so a key storing it is
  // refused here by name rather than verifying against the wrong protocol.
  [Provider.Alibaba]: {
    [LlmWire.Anthropic]: ['https://token-plan.ap-southeast-1.maas.aliyuncs.com/apps/anthropic'],
    [LlmWire.OpenAI]: [
      'https://token-plan.ap-southeast-1.maas.aliyuncs.com/compatible-mode/v1',
      'https://dashscope-us.aliyuncs.com/compatible-mode/v1',
      'https://dashscope-intl.aliyuncs.com/compatible-mode/v1',
      'https://dashscope.aliyuncs.com/compatible-mode/v1',
    ],
  },
  /** Router gateway speaks Anthropic Messages and OpenAI `/v1` (#1904). */
  [Provider.Router]: {
    [LlmWire.Anthropic]: ['https://router.ai.developerz.ai'],
    [LlmWire.OpenAI]: ['https://router.ai.developerz.ai/v1'],
  },
  [Provider.Custom]: {},
};

/**
 * The wires each provider's in-cluster client factory (`@developerz/agent`) can
 * SPEAK. {@link wireFor} only moves a key onto a wire listed here, and billing's
 * capture gate refuses a recognized endpoint on any other. `minimax` and `custom`
 * speak both; every other provider speaks only its {@link PROVIDER_WIRE} wire —
 * which is why a zai key stored on the OpenAI coding path, a router key on
 * `/v1`, or an alibaba key on `/apps/anthropic` keeps its default wire (and is
 * refused at capture) rather than being sealed onto a wire its client cannot
 * dial. Widening a provider here is a claim about its factory, so it ships with
 * that factory.
 */
export const PROVIDER_SUPPORTED_WIRES: Record<BYOKProvider, readonly LlmWire[]> = {
  [Provider.Anthropic]: [PROVIDER_WIRE[Provider.Anthropic].wire],
  [Provider.Zai]: [PROVIDER_WIRE[Provider.Zai].wire],
  [Provider.OpenAI]: [PROVIDER_WIRE[Provider.OpenAI].wire],
  [Provider.OpenRouter]: [PROVIDER_WIRE[Provider.OpenRouter].wire],
  [Provider.Cerebras]: [PROVIDER_WIRE[Provider.Cerebras].wire],
  [Provider.Minimax]: [LlmWire.Anthropic, LlmWire.OpenAI],
  [Provider.Gemini]: [PROVIDER_WIRE[Provider.Gemini].wire],
  [Provider.Kimi]: [PROVIDER_WIRE[Provider.Kimi].wire],
  [Provider.Groq]: [PROVIDER_WIRE[Provider.Groq].wire],
  [Provider.Xai]: [PROVIDER_WIRE[Provider.Xai].wire],
  [Provider.Deepseek]: [PROVIDER_WIRE[Provider.Deepseek].wire],
  [Provider.Together]: [PROVIDER_WIRE[Provider.Together].wire],
  [Provider.Fireworks]: [PROVIDER_WIRE[Provider.Fireworks].wire],
  [Provider.Router]: [PROVIDER_WIRE[Provider.Router].wire],
  [Provider.Alibaba]: [PROVIDER_WIRE[Provider.Alibaba].wire],
  [Provider.Custom]: [LlmWire.Anthropic, LlmWire.OpenAI],
};

/** A recognized endpoint's wire, its auth style, and the provider whose set lists it. */
export type RecognizedEndpoint = { wire: LlmWire; authStyle: LlmAuthStyle; owner: BYOKProvider };

/**
 * Index a recognized-endpoint table by normalized endpoint. Pure, and exported only
 * so its refusals are testable against a synthetic table; the platform's index is
 * {@link RECOGNIZED_BY_ENDPOINT}. An entry's auth style is its owner's
 * {@link PROVIDER_WIRE} `authStyle`.
 *
 * THROWS on an entry that does not parse, and on one URL filed by two owners that
 * disagree on the WIRE or on the AUTH STYLE: either conflict would make
 * {@link wireFor} answer by iteration order — the first owner silently winning.
 */
export function indexRecognizedEndpoints(
  table: Readonly<
    Partial<Record<BYOKProvider, Readonly<Partial<Record<LlmWire, readonly string[]>>>>>
  >,
): ReadonlyMap<string, RecognizedEndpoint> {
  const index = new Map<string, RecognizedEndpoint>();
  for (const [owner, sets] of Object.entries(table) as Array<
    [BYOKProvider, Partial<Record<LlmWire, readonly string[]>>]
  >) {
    const authStyle = PROVIDER_WIRE[owner].authStyle;
    for (const [wire, urls] of Object.entries(sets) as Array<[LlmWire, readonly string[]]>) {
      for (const url of urls) {
        const normalized = normalizeLlmEndpoint(url);
        if (normalized === null) {
          throw new Error(`PROVIDER_RECOGNIZED_ENDPOINTS[${owner}][${wire}]: unparseable "${url}"`);
        }
        const prior = index.get(normalized);
        if (prior && (prior.wire !== wire || prior.authStyle !== authStyle)) {
          throw new Error(
            `PROVIDER_RECOGNIZED_ENDPOINTS: "${url}" is filed under ` +
              `${prior.owner}/${prior.wire}/${prior.authStyle} and ${owner}/${wire}/${authStyle} — ` +
              'one endpoint speaks one wire with one auth style; drop one entry or fix its owner',
          );
        }
        if (!prior) index.set(normalized, { wire, authStyle, owner });
      }
    }
  }
  return index;
}

/** The platform's index, built once at load — a conflict in the table fails the boot. */
const RECOGNIZED_BY_ENDPOINT = indexRecognizedEndpoints(PROVIDER_RECOGNIZED_ENDPOINTS);

function recognizedEndpoint(normalized: string): RecognizedEndpoint | undefined {
  return RECOGNIZED_BY_ENDPOINT.get(normalized);
}

/**
 * The wire a recognized first-party endpoint speaks, whoever owns it; `null`
 * for an endpoint in no set (a gateway, an unlisted host) or an unparseable
 * string. What billing's capture gate asks before it compares against
 * {@link PROVIDER_SUPPORTED_WIRES}.
 */
export function endpointWire(baseUrl: string): LlmWire | null {
  const normalized = normalizeLlmEndpoint(baseUrl);
  return normalized === null ? null : (recognizedEndpoint(normalized)?.wire ?? null);
}

/** How a key is dialled: the two wire-policy fields a sealed credential carries. */
export type LlmWirePolicy = { wire: LlmWire; authStyle: LlmAuthStyle };

/**
 * THE seam for "which wire and auth does this key dial" (plan 102 slice 04, K8).
 * The endpoint decides when the platform recognizes it; the provider label
 * decides otherwise.
 *
 * 1. Resolve the base: the key's `baseUrl`, else the provider's default.
 * 2. Look it up (normalized) in {@link PROVIDER_RECOGNIZED_ENDPOINTS}, searched
 *    across ALL providers — so a `custom` key on Alibaba's `/apps/anthropic`
 *    finds alibaba's Anthropic entry.
 * 3. A recognized wire the provider SUPPORTS ({@link PROVIDER_SUPPORTED_WIRES})
 *    and that differs from its default wins, with the owning provider's auth
 *    style (every owner of an Anthropic-wire entry is bearer). Anything else —
 *    unrecognized, the provider's own wire, or a wire its factory cannot speak —
 *    is `PROVIDER_WIRE[provider]` unchanged, auth included.
 *
 * Why step 3 keeps the default auth on a same-wire match: an `anthropic`-labelled
 * key pointed at the router keeps `x-api-key`, as it did before this seam.
 *
 * Prod 2026-09-22 03:28–03:41Z: `minimax` key 1566f5b1 on
 * `https://api.minimax.io/anthropic` sealed `openai` from its label and failed 28
 * runs across 10 tasks (mitigated by clearing its `base_url`). The `custom` key
 * cf1eb8b6 on Alibaba's `…/apps/anthropic` had the same defect and had not yet run.
 */
export function wireFor(provider: BYOKProvider, baseUrl: string | null): LlmWirePolicy {
  const policy = PROVIDER_WIRE[provider];
  const fallback: LlmWirePolicy = { wire: policy.wire, authStyle: policy.authStyle };
  const base = baseUrl ?? policy.defaultBaseUrl;
  const normalized = base === null ? null : normalizeLlmEndpoint(base);
  const found = normalized === null ? undefined : recognizedEndpoint(normalized);
  if (!found || found.wire === policy.wire) return fallback;
  if (!PROVIDER_SUPPORTED_WIRES[provider].includes(found.wire)) return fallback;
  return { wire: found.wire, authStyle: found.authStyle };
}

// ─── providers with no default endpoint ─────────────────────────────────────

/**
 * Why a provider's `base_url` cannot be dialled. Two kinds, because they are
 * two different fixes: `base_url_required` — type one in; `base_url_unrecognized`
 * — the one typed is not an endpoint of this provider (use `custom` for a
 * gateway). `reason` is the sentence a person reads and names the shapes to use.
 */
export type ProviderBaseUrlVerdict =
  | { ok: true }
  | { ok: false; kind: 'base_url_required' | 'base_url_unrecognized'; reason: string };

/**
 * Model Studio's OpenAI-compatible base on a fixed host. Sourced from
 * https://www.alibabacloud.com/help/en/model-studio/compatibility-of-openai-with-dashscope
 * (read 2026-09-11): `dashscope-us` is Virginia's endpoint; `dashscope-intl` and
 * `dashscope` are the legacy domains the page says "remain available" (no sunset
 * date given). All three answered an unauthenticated `GET …/compatible-mode/v1/models`
 * with 401 on 2026-09-11 — the route exists and demands a key.
 *
 * The Coding Plan host (`coding-intl.dashscope.aliyuncs.com/v1`) is deliberately
 * ABSENT: its `GET /v1/models` answered 200 with the model list to a request
 * carrying NO key (2026-09-11), so the verify probe's auth leg would pass any
 * string pasted as a key there. A Coding Plan key stays on `custom`.
 */
const ALIBABA_FIXED_HOSTS: ReadonlySet<string> = new Set([
  'dashscope-us.aliyuncs.com',
  'dashscope-intl.aliyuncs.com',
  'dashscope.aliyuncs.com',
]);

/**
 * `{WorkspaceId}.<region>.maas.aliyuncs.com` — the workspace-scoped host every
 * other region now serves on (same page: Singapore `ap-southeast-1`, Beijing
 * `cn-beijing`, Hong Kong `cn-hongkong`, Tokyo `ap-northeast-1`), and the Token
 * Plan's `token-plan.<region>.maas.aliyuncs.com`. EXACTLY two labels in front,
 * anchored at both ends, so `a.b.<region>.maas…` and `…maas.aliyuncs.com.evil`
 * both miss.
 */
const ALIBABA_WORKSPACE_HOST = /^[a-z0-9-]+\.[a-z0-9-]+\.maas\.aliyuncs\.com$/;

/** The one path every recognized Model Studio host serves the OpenAI wire under. */
const ALIBABA_OPENAI_PATH = '/compatible-mode/v1';

const ALIBABA_ENDPOINT_SHAPES =
  'https://{WorkspaceId}.ap-southeast-1.maas.aliyuncs.com/compatible-mode/v1 (Singapore), ' +
  'https://dashscope-us.aliyuncs.com/compatible-mode/v1 (US Virginia), or ' +
  'https://{WorkspaceId}.cn-beijing.maas.aliyuncs.com/compatible-mode/v1 (China Beijing); ' +
  'Hong Kong and Tokyo use the same {WorkspaceId}.<region>.maas.aliyuncs.com shape ' +
  '(cn-hongkong, ap-northeast-1), and a Token Plan key uses ' +
  'https://token-plan.ap-southeast-1.maas.aliyuncs.com/compatible-mode/v1';

/**
 * The base is used VERBATIM downstream: the verify probe appends `/models` to the
 * raw string (`verifyUrlFor`, `@developerz/billing`) and the SDK appends
 * `/chat/completions`. So the string must END at the path: no userinfo (a
 * credential riding in the authority), no query and no fragment (both checked
 * on the RAW string, since `URL` normalizes a bare `?`/`#` away and the append
 * would still land after it), and at most ONE trailing slash (`…//models`).
 */
function isAlibabaOpenAiEndpoint(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' || url.port !== '') return false;
  if (url.username !== '' || url.password !== '') return false;
  if (raw.includes('?') || raw.includes('#')) return false;
  const host = url.hostname.toLowerCase();
  if (!ALIBABA_FIXED_HOSTS.has(host) && !ALIBABA_WORKSPACE_HOST.test(host)) return false;
  return url.pathname === ALIBABA_OPENAI_PATH || url.pathname === `${ALIBABA_OPENAI_PATH}/`;
}

/**
 * True when `raw` is a Coding Plan / token-plan endpoint REJECTED by the
 * alibaba capture gate. Rationale (#3818), per host:
 *   - `coding-intl.dashscope.aliyuncs.com` — no-key `GET /v1/models`
 *     answered 200 on 2026-09-11, so the verify probe would pass any string
 *     pasted as a key.
 *   - `token-plan.<region>.maas.aliyuncs.com/apps/anthropic` — Anthropic wire,
 *     not OpenAI-compatible.
 * Register as `provider: "custom"`.
 */
function isAlibabaCodingPlanHost(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' || url.port !== '') return false;
  const host = url.hostname.toLowerCase();
  if (host === 'coding-intl.dashscope.aliyuncs.com') return true;
  if (host.includes('token-plan') && host.endsWith('.aliyuncs.com')) {
    return url.pathname.startsWith('/apps/anthropic');
  }
  return false;
}

/**
 * THE seam for "can this provider's key be dialled at this `base_url`" — the
 * providers whose {@link PROVIDER_WIRE} entry has no default host. Every
 * capture ingress (REST, personal plane, MCP) and the verify probe ask this one
 * function, so the refusal and its sentence exist once.
 *
 * - `custom` — any endpoint, but one is required (an arbitrary OpenAI-compatible
 *   endpoint has nothing to default to). The host is the SSRF gate's business.
 * - `alibaba` — required AND recognized: a Model Studio OpenAI-compatible base
 *   on {@link ALIBABA_FIXED_HOSTS} or the workspace-scoped host shape, over
 *   https, at exactly `/compatible-mode/v1`. An unrelated host under the
 *   `alibaba` label is refused rather than dialled with the customer's secret —
 *   a gateway fronting Qwen is a `custom` key. An unparseable URL is refused
 *   too: "recognized" cannot be true of a string that is not a URL.
 * - every other provider has a default host, so absent means "use it" and an
 *   override stays the SSRF and wire gates' business: always `ok`.
 */
export function vetProviderBaseUrl(
  provider: BYOKProvider,
  baseUrl: string | null | undefined,
): ProviderBaseUrlVerdict {
  if (provider === Provider.Custom) {
    if (baseUrl) return { ok: true };
    return {
      ok: false,
      kind: 'base_url_required',
      reason:
        'base_url_required (vetProviderBaseUrl): provider "custom" is an arbitrary ' +
        'OpenAI-compatible endpoint with no default — set base_url to its /v1 base',
    };
  }
  if (provider === Provider.Alibaba) {
    if (!baseUrl) {
      return {
        ok: false,
        kind: 'base_url_required',
        reason:
          'base_url_required (vetProviderBaseUrl): an "alibaba" key has no default ' +
          'endpoint — Model Studio scopes its OpenAI-compatible endpoint to your ' +
          `workspace and region. Set base_url to ${ALIBABA_ENDPOINT_SHAPES}`,
      };
    }
    if (isAlibabaOpenAiEndpoint(baseUrl)) return { ok: true };
    if (isAlibabaCodingPlanHost(baseUrl)) {
      return {
        ok: false,
        kind: 'base_url_unrecognized',
        reason:
          `base_url_unrecognized (vetProviderBaseUrl): base_url "${baseUrl}" is not a Model ` +
          'Studio OpenAI-compatible endpoint. Coding Plan keys are not admissible ' +
          'under provider "alibaba": coding-intl answers /v1/models without a key ' +
          '(verify would pass any string), while /apps/anthropic speaks the Anthropic ' +
          'wire "alibaba" does not support. Register it as provider "custom" with ' +
          'this same base_url instead.',
      };
    }
    return {
      ok: false,
      kind: 'base_url_unrecognized',
      reason:
        `base_url_unrecognized (vetProviderBaseUrl): base_url "${baseUrl}" is not a Model ` +
        `Studio OpenAI-compatible endpoint. Use ${ALIBABA_ENDPOINT_SHAPES}; for a gateway ` +
        'in front of Qwen, add the key as provider "custom"',
    };
  }
  return { ok: true };
}
